import React from 'react';
import styled from 'styled-components';
import { injectGlobal } from 'react-emotion';

import imgFleet from '../assets/img/pillar-fleet.jpg';
import imgPlatform from '../assets/img/pillar-platform.jpg';
import imgConcierge from '../assets/img/concierge-service.jpg';

/* global tw */

/*
 * "What Zyppys is" — category-creation section, redesigned as an
 * alternating image/text showcase. Each pillar is a full-width row with a
 * large photo on one side and the copy on the other, sides alternating.
 * On scroll-in (per row): the image clip-wipes open + settle-zooms and the
 * text slides in. Falls back to fully-revealed for no-JS / reduced-motion.
 */

injectGlobal`
    @keyframes pillarReveal { from { clip-path: inset(0 0 100% 0 round 18px); } to { clip-path: inset(0 0 0 0 round 18px); } }
    @keyframes pillarZoom   { from { transform: scale(1.12); } to { transform: scale(1); } }
    @keyframes pillarText   { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
`

const Section = styled.section`
    ${tw`w-full relative z-10`};
    background: #0A0A0F;
    padding: 6rem 0 5.5rem;
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    @media (max-width: 991px){
        padding: 4rem 0 4rem;
    }
`

const Inner = styled.div`
    ${tw`w-full mx-auto px-4 xl:px-0`};
    max-width: 70rem;
`

const Eyebrow = styled.div`
    ${tw`inline-flex items-center`};
    font-weight: 700;
    font-size: 0.8125rem;
    letter-spacing: 0.24em;
    text-transform: uppercase;
    color: #C9A2FF;
    &::before {
        content: '';
        display: inline-block;
        width: 1.6rem;
        height: 2px;
        background: #B15CFF;
        margin-right: 0.7rem;
    }
`

const Title = styled.h2`
    ${tw`m-0`};
    font-weight: 800;
    font-size: 2.25rem;
    line-height: 1.08;
    letter-spacing: -0.035em;
    color: #FFFFFF;
    margin-top: 1rem;
    max-width: 50rem;
    @media (min-width: 992px){
        font-size: 3.5rem;
    }
    strong {
        background: linear-gradient(135deg, #D8B4FE 0%, #B15CFF 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        font-weight: 800;
    }
`

const Lead = styled.p`
    font-weight: 500;
    font-size: 1.1875rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.75);
    margin-top: 1.5rem;
    max-width: 44rem;
`

const Rows = styled.div`
    ${tw`w-full`};
    margin-top: 4rem;
    @media (max-width: 991px){ margin-top: 2.5rem; }
`

const PillarRow = styled.div`
    ${tw`w-full flex flex-wrap items-center`};
    gap: 2.5rem;
    padding: 1.5rem 0;
    & + & { margin-top: 2.5rem; }
    @media (min-width: 992px){
        flex-wrap: nowrap;
        gap: 4rem;
        ${props => props.reverse && 'flex-direction: row-reverse;'}
    }

    .media {
        ${tw`relative w-full`};
        flex: 1 1 0;
        min-width: 0;
        height: 22rem;
        border-radius: 18px;
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.08);
        @media (max-width: 991px){ height: 15rem; }
    }
    .pillar-img {
        ${tw`absolute w-full h-full`};
        top: 0; left: 0;
        object-fit: cover;
    }
    .media::after {
        content: '';
        ${tw`absolute`};
        inset: 0;
        background: linear-gradient(115deg, rgba(10,10,15,0.35) 0%, rgba(10,10,15,0) 45%);
    }

    .ptext {
        ${tw`w-full`};
        flex: 1 1 0;
        min-width: 0;
    }
    .pnum {
        display: inline-block;
        font-weight: 800;
        font-size: 1rem;
        letter-spacing: 0.04em;
        background: linear-gradient(135deg, #D8B4FE 0%, #B15CFF 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }
    .ptext h3 {
        ${tw`m-0`};
        font-weight: 700;
        font-size: 1.75rem;
        letter-spacing: -0.025em;
        color: #FFFFFF;
        margin-top: 0.75rem;
        @media (min-width: 992px){ font-size: 2.25rem; }
    }
    .ptext p {
        font-weight: 500;
        font-size: 1.0625rem;
        line-height: 1.6;
        color: rgba(255, 255, 255, 0.68);
        margin-top: 0.9rem;
        margin-bottom: 0;
        max-width: 26rem;
    }

    /* ---- armed (JS mounted, pre-play) ---- */
    &.armed .media { clip-path: inset(0 0 100% 0 round 18px); }
    &.armed .pillar-img { transform: scale(1.12); }
    &.armed .pnum,
    &.armed .ptext h3,
    &.armed .ptext p { opacity: 0; transform: translateY(20px); }

    /* ---- in-view: play ---- */
    &.armed.in-view .media { animation: pillarReveal 0.95s cubic-bezier(.7, 0, .2, 1) both; }
    &.armed.in-view .pillar-img { animation: pillarZoom 1.6s ease-out both; }
    &.armed.in-view .pnum   { animation: pillarText 0.6s ease-out both; animation-delay: 0.35s; }
    &.armed.in-view .ptext h3 { animation: pillarText 0.6s ease-out both; animation-delay: 0.45s; }
    &.armed.in-view .ptext p  { animation: pillarText 0.6s ease-out both; animation-delay: 0.55s; }

    @media (prefers-reduced-motion: reduce) {
        &.armed .media { clip-path: none !important; animation: none !important; }
        &.armed .pillar-img { transform: none !important; animation: none !important; }
        &.armed .pnum, &.armed .ptext h3, &.armed .ptext p {
            opacity: 1 !important; transform: none !important; animation: none !important;
        }
    }
`

const PILLARS = [
    {
        num: '01',
        img: imgFleet,
        title: 'Luxury Fleet',
        copy: 'Premium cars with professional chauffeurs — immaculately maintained and always on time.',
    },
    {
        num: '02',
        img: imgPlatform,
        title: 'Smart Platform',
        copy: 'Real-time tracking, dashboards, and automation that keep every movement under control.',
    },
    {
        num: '03',
        img: imgConcierge,
        title: 'Concierge Support',
        copy: 'A dedicated team managing every pickup, drop, and detail — end to end.',
    },
];

class SectionPillars extends React.Component {
    constructor(props) {
        super(props);
        this.state = { armed: false, inView: [false, false, false] };
        this.rowRefs = PILLARS.map(() => React.createRef());
    }

    componentDidMount() {
        this.setState({ armed: true });
        if (typeof IntersectionObserver === 'undefined') {
            this.setState({ inView: PILLARS.map(() => true) });
            return;
        }
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
                    const idx = Number(entry.target.getAttribute('data-index'));
                    this.setState((prev) => {
                        const inView = prev.inView.slice();
                        inView[idx] = true;
                        return { inView };
                    });
                    this.observer.unobserve(entry.target);
                }
            });
        }, { threshold: [0, 0.2] });
        this.rowRefs.forEach((r) => { if (r.current) { this.observer.observe(r.current); } });
    }

    componentWillUnmount() {
        if (this.observer) { this.observer.disconnect(); }
    }

    render() {
        return (
            <Section>
                <Inner>
                    <Eyebrow>What Zyppys is</Eyebrow>
                    <Title>Not a Car Rental.<br /><strong>A Guest Transportation Concierge.</strong></Title>
                    <Lead>
                        Zyppys goes beyond vehicles. We manage your entire guest travel experience &mdash;
                        from airport arrivals to venue transfers &mdash; with precision, technology, and premium service.
                    </Lead>
                    <Rows>
                        {PILLARS.map((p, i) => (
                            <PillarRow
                                key={p.num}
                                data-index={i}
                                ref={this.rowRefs[i]}
                                reverse={i % 2 === 1}
                                className={`${this.state.armed ? 'armed' : ''} ${this.state.inView[i] ? 'in-view' : ''}`.trim()}
                            >
                                <div className="media">
                                    <img className="pillar-img" src={p.img} alt={p.title} />
                                </div>
                                <div className="ptext">
                                    <span className="pnum">{p.num}</span>
                                    <h3>{p.title}</h3>
                                    <p>{p.copy}</p>
                                </div>
                            </PillarRow>
                        ))}
                    </Rows>
                </Inner>
            </Section>
        );
    }
}

export default SectionPillars;
